/**
 * Centralized Command Configuration System
 * =========================================
 *
 * This module provides an extensible way to configure commands in the cluster operations
 * command palette (Cmd+K). It supports three types of commands:
 *
 * 1. **Regular Operations**: Commands that directly map to cluster operations
 * 2. **Action Commands**: Special commands that execute custom logic (e.g., open links)
 * 3. **Alias Commands**: Commands that show with a custom name/form but execute as a different operation
 *
 */

import type { Cluster } from "~/types";
import {
  getDatadogClusterDashboardUrl,
  getDatadogClusterLogsUrl,
  getDatadogClusterTracesUrl,
  getSessionToken,
} from "~/utils";
import { operationNotes } from "~/utils/operationUtils";
import { slackChannelUrl } from "~/utils/slack";

export type CommandType = "operation" | "alias" | "action";

/**
 * Argument definition for command/operation parameters
 */
export type ArgumentDefinition = Record<
  string,
  {
    type: string;
    required?: boolean;
    defaultValue?: string | number;
  }
>;

export interface CommandConfig {
  /** The internal command identifier */
  id: string;

  /** Display name shown in the command palette */
  displayName: string;

  /** Type of command */
  type: CommandType;

  /**
   * Condition to determine if this command should be shown
   * @param clusters - The selected clusters
   * @returns true if the command should be shown
   */
  shouldShow?: (clusters: Cluster[]) => boolean;

  /**
   * For 'action' type: the function to execute
   * Note: The dialog will be automatically closed after the action executes
   * @param clusters - The selected clusters
   * @param openModal - Function to open a modal (optional)
   */
  action?: (
    clusters: Cluster[],
    openModal?: (modalType: any) => void,
    navigate?: (path: string) => void,
  ) => void;

  /**
   * Optional: For alias commands, the operation actually submitted, so the
   * palette can look up its advertised approval behavior.
   */
  operation?: string;

  /**
   * Optional: For alias commands, whether a stored operation was submitted
   * through this alias, so history can show it by the alias's name.
   */
  matchesOperation?: (args: Record<string, any>) => boolean;

  /**
   * Optional: For alias commands whose stored arguments differ from the form,
   * maps them back to form values so a retry prefills the fields.
   */
  retryArguments?: (args: Record<string, any>) => Record<string, any>;

  /**
   * Optional: Synthetic arguments for alias commands
   * Defines the form fields that will be shown to the user
   */
  syntheticArguments?: ArgumentDefinition;

  /**
   * Optional: Transform function for alias commands
   * Converts the synthetic form data into the actual operation format
   */
  transformFormData?: (jsonData: Record<string, any>) => Record<string, any>;

  /**
   * Optional: Description/note to show above the form. A function receives
   * the selected clusters, for notes that depend on the selection.
   */
  formNote?: string | ((clusters: Cluster[]) => string);
}

/**
 * Preferred ordering for operations in the command palette
 * Operations not in this list will be shown alphabetically after these
 */
export const OPERATIONS_ORDER = [
  "lock",
  "unlock",
  "upgrade_latest",
  "upgrade",
  "reindex",
  "warm_cache",
  "scale",
  "rolling_restart",
  "force_restart",
  "restart",
  "lsm_gc",
  "get_config",
  "set_config",
  "compact_wal",
  "create_api_key", // Ensure this comes before alphabetical sorting
];

const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

export function generateOperationShortcuts(
  operations: string[],
  reservedLetters?: string[],
) {
  const isReservedletter = (letter: string) => {
    if (reservedLetters && reservedLetters.length > 0) {
      return reservedLetters.includes(letter);
    }
    return false;
  };

  // Sort: OPERATIONS_ORDER first, then remaining alphabetically
  const sortedOps = [
    ...OPERATIONS_ORDER.filter((op) => operations.includes(op)),
    ...operations.filter((op) => !OPERATIONS_ORDER.includes(op)).sort(),
  ];

  return sortedOps.reduce(
    (acc, op) => {
      // Custom shortcuts for specific commands
      const customShortcuts: Record<string, string> = {
        upgrade_latest: "A",
        upgrade: "U",
        warm_cache: "H",
        tidy: "T",
      };

      if (customShortcuts[op] && !acc[customShortcuts[op]]) {
        acc[customShortcuts[op]] = op;
        return acc;
      }

      // Try first letter of operation name
      const firstLetter = op[0].toUpperCase();
      if (!acc[firstLetter] && !isReservedletter(firstLetter)) {
        acc[firstLetter] = op;
        return acc;
      }

      // Try first letter after space
      const spaceIndex = op.indexOf(" ");
      if (spaceIndex !== -1 && spaceIndex < op.length - 1) {
        const spaceLetter = op[spaceIndex + 1].toUpperCase();
        if (!acc[spaceLetter] && !isReservedletter(spaceLetter)) {
          acc[spaceLetter] = op;
          return acc;
        }
      }

      // Try first letter after underscore
      const underscoreIndex = op.indexOf("_");
      if (underscoreIndex !== -1 && underscoreIndex < op.length - 1) {
        const underscoreLetter = op[underscoreIndex + 1].toUpperCase();
        if (!acc[underscoreLetter] && !isReservedletter(underscoreLetter)) {
          acc[underscoreLetter] = op;
          return acc;
        }
      }

      // If all else fails, use next available letter
      for (const letter of letters) {
        if (!acc[letter] && !isReservedletter(letter)) {
          acc[letter] = op;
          break;
        }
      }
      return acc;
    },
    {} as Record<string, string>,
  );
}

/**
 * Whether the cluster's agent accepts restart's `all_at_once` argument. Remove once
 * the new agent is rolled out everywhere.
 */
function advertisesRestartAllAtOnce(cluster: Cluster): boolean {
  const args = cluster.possible_operations.restart?.arguments;
  return typeof args === "object" && args !== null && "all_at_once" in args;
}

export function advertisesRestartPods(cluster: Cluster): boolean {
  const args = cluster.possible_operations.restart?.arguments;
  return typeof args === "object" && args !== null && "pods" in args;
}

/** Whether the operation should be hidden based on the state of the clusters. */
export function isOperationHidden(
  operation: string,
  clusters: Cluster[],
): boolean {
  return operation === "restart" && clusters.some(advertisesRestartAllAtOnce);
}

/**
 * Centralized command configuration
 * Add new commands, aliases, or actions here
 */
export const COMMAND_CONFIGS: CommandConfig[] = [
  {
    id: "search_orgs",
    displayName: "Search Organizations",
    type: "action",
    shouldShow: () => true,
    action: (_clusters, openModal) => {
      openModal?.("org-search");
    },
  },
  {
    id: "search_customers",
    displayName: "Search Customers",
    type: "action",
    shouldShow: () => true,
    action: (_clusters, openModal) => {
      openModal?.("customer-search");
    },
  },
  {
    id: "search_namespace",
    displayName: "Search Namespaces",
    type: "action",
    shouldShow: () => true,
    action: (_clusters, openModal) => {
      openModal?.("namespace-search");
    },
  },
  {
    id: "storage_prefix",
    displayName: "Compute Storage Prefix",
    type: "action",
    shouldShow: () => true,
    action: (_clusters, openModal) => {
      openModal?.("storage-prefix");
    },
  },
  {
    id: "view_version_gates",
    displayName: "View Version Gates",
    type: "action",
    shouldShow: () => true,
    action: (_clusters, _openModal, navigate) => {
      navigate?.("/version-gates");
    },
  },

  {
    id: "copy_session_token",
    displayName: "Copy Session Token",
    type: "action",
    shouldShow: () => true,
    action: async () => {
      try {
        const token = await getSessionToken();
        if (token && navigator.clipboard) {
          await navigator.clipboard.writeText(token);
        }
      } catch (error) {
        // Silently handle errors in test environments
        console.error("Failed to copy session token:", error);
      }
    },
  },

  // Customer Operations (show only when no clusters selected)
  {
    id: "create_api_key",
    displayName: "Create Customer API Key",
    type: "action",
    shouldShow: (clusters) => clusters.length === 0,
    action: (_clusters, openModal) => {
      openModal?.("api");
    },
  },
  {
    id: "dashboard_invite",
    displayName: "Attach Org to Customer",
    type: "action",
    shouldShow: (clusters) => clusters.length === 0,
    action: (_clusters, openModal) => {
      openModal?.("dashboard");
    },
  },
  {
    id: "attach_slack_channel",
    displayName: "Attach Slack Channel",
    type: "action",
    shouldShow: (clusters) => clusters.length === 0,
    action: (_clusters, openModal) => {
      openModal?.("slack");
    },
  },
  {
    id: "generate_byoc_kit",
    displayName: "Generate BYOC Kit",
    type: "action",
    shouldShow: (clusters) => clusters.length === 0,
    action: (_clusters, openModal) => {
      openModal?.("byoc-kit");
    },
  },
  // Global action: always available, independent of cluster selection.
  {
    id: "register_cluster",
    displayName: "Register Cluster",
    type: "action",
    shouldShow: () => true,
    action: (_clusters, openModal) => {
      openModal?.("register-cluster");
    },
  },

  // Open Slack Channel (for selected clusters)
  {
    id: "open_slack_channel",
    displayName: "Open Slack Channel",
    type: "action",
    shouldShow: (clusters) =>
      clusters.length > 0 && clusters.some((c) => c.slack_channel),
    action: (clusters) => {
      const slackChannel = clusters[0]?.slack_channel;
      if (slackChannel) {
        window.open(slackChannelUrl(slackChannel), "_self");
      }
    },
  },

  // Datadog Links (for selected clusters)
  {
    id: "open_datadog_metrics",
    displayName: "Open Datadog Metrics",
    type: "action",
    shouldShow: (clusters) => {
      if (clusters.length === 0) return false;
      const url = getDatadogClusterDashboardUrl(clusters);
      return url !== null;
    },
    action: (clusters) => {
      const url = getDatadogClusterDashboardUrl(clusters);
      if (url) {
        window.open(url, "_blank");
      }
    },
  },
  {
    id: "open_datadog_logs",
    displayName: "Open Datadog Logs",
    type: "action",
    shouldShow: (clusters) => {
      if (clusters.length === 0) return false;
      const url = getDatadogClusterLogsUrl(clusters);
      return url !== null;
    },
    action: (clusters) => {
      const url = getDatadogClusterLogsUrl(clusters);
      if (url) {
        window.open(url, "_blank");
      }
    },
  },
  {
    id: "open_datadog_traces",
    displayName: "Open Datadog Traces",
    type: "action",
    shouldShow: (clusters) => {
      if (clusters.length === 0) return false;
      const url = getDatadogClusterTracesUrl(clusters);
      return url !== null;
    },
    action: (clusters) => {
      const url = getDatadogClusterTracesUrl(clusters);
      if (url) {
        window.open(url, "_blank");
      }
    },
  },

  // Two faces of "restart", offered only when every agent advertises pods.
  {
    id: "rolling_restart",
    displayName: "rolling-restart",
    type: "alias",
    operation: "restart",
    matchesOperation: (args) => args.all_at_once === false,
    shouldShow: (clusters) =>
      clusters.length > 0 && clusters.every(advertisesRestartPods),
    formNote:
      "Pods are replaced gradually, respecting each workload's update strategy and disruption budgets.",
    syntheticArguments: {
      name_prefix: {
        type: "string",
        required: false,
      },
      query_deployment: {
        type: "string",
        required: false,
      },
      pods: {
        type: "string_array",
        required: false,
      },
    },
    transformFormData: (jsonData) => ({
      ...jsonData,
      operation: "restart",
      all_at_once: false,
    }),
  },
  {
    id: "force_restart",
    displayName: "force-restart",
    type: "alias",
    operation: "restart",
    matchesOperation: (args) => args.all_at_once === true,
    // Force-restart picks pods, which only exist per cluster.
    shouldShow: (clusters) =>
      clusters.length === 1 && advertisesRestartPods(clusters[0]),
    formNote:
      "WARNING: This is a forced restart and can cause downtime. Each pod is recreated by its own controller.",
    syntheticArguments: {
      name_prefix: {
        type: "string",
        required: false,
      },
      query_deployment: {
        type: "string",
        required: false,
      },
      pods: {
        type: "string_array",
        required: false,
      },
    },
    transformFormData: (jsonData) => ({
      ...jsonData,
      operation: "restart",
      all_at_once: true,
    }),
  },

  // "Recall Replay" that executes "tidy" with pre-filled + custom arguments
  // This shows a custom command name in the palette but executes as "tidy" operation
  {
    id: "recall_replay",
    displayName: "Recall Replay",
    type: "alias",
    operation: "tidy",
    matchesOperation: (args) => args.job === "recall-replay-new",
    retryArguments: (args) => {
      const flags: Record<string, string> = {};
      for (const arg of Array.isArray(args.args) ? args.args : []) {
        const match = /^--([^=]+)=(.*)$/.exec(String(arg));
        if (match) flags[match[1]] = match[2];
      }
      return {
        orgId: flags["org-id"],
        namespace: flags["namespace"],
        limit: flags["limit"],
      };
    },
    shouldShow: (clusters) =>
      clusters.length > 0 &&
      clusters.every((c) => "tidy" in c.possible_operations),
    formNote:
      "Execute the recall-replay-new tidy job with customizable parameters:",
    syntheticArguments: {
      namespace: {
        type: "string",
        required: true,
      },
      orgId: {
        type: "string",
        required: true,
      },
      limit: {
        type: "int",
        required: true,
        defaultValue: "10",
      },
    },
    transformFormData: (jsonData) => {
      return {
        operation: "tidy",
        job: "recall-replay-new",
        args: [
          `--org-id=${jsonData.orgId}`,
          `--namespace=${jsonData.namespace}`,
          `--limit=${jsonData.limit}`,
        ],
        envs: [],
      };
    },
  },

  // "Edit Tags" — rendered with a key/value editor and submitted as a tags
  // merge patch, not a backend operation.
  {
    id: "edit_tags",
    displayName: "Edit Tags",
    type: "alias",
    shouldShow: (clusters) => clusters.length > 0,
    formNote: (clusters) =>
      clusters.length === 1
        ? "Add, edit, or remove this cluster's custom tags. Derived tags (kind, customer) can't be overridden."
        : "Upserts the entered tags on every selected cluster: only these keys are set, other tags on each cluster are kept. To remove a tag, edit clusters individually.",
    syntheticArguments: {
      tags: {
        type: "string",
        required: false,
      },
    },
    transformFormData: (jsonData) => {
      return {
        operation: "edit_tags",
        tags: jsonData.tags ?? "",
      };
    },
  },
];

/**
 * Get all commands that should be shown for the given clusters
 * This includes both configured commands and regular operations, sorted by preferred order
 */
export function getAvailableCommands(
  clusters: Cluster[],
  possibleOperations: Record<string, any>,
): { commands: string[] } {
  // Get regular operations available on ALL clusters (only if clusters exist)
  const commonOperations =
    clusters.length > 0
      ? Object.keys(possibleOperations).filter(
          (op) =>
            !isOperationHidden(op, clusters) &&
            clusters.every((cluster) => op in cluster.possible_operations),
        )
      : [];

  // Get configured commands that should be shown (works for 0 or more clusters)
  const configuredCommands = COMMAND_CONFIGS.filter(
    (cmd) => !cmd.shouldShow || cmd.shouldShow(clusters),
  ).map((cmd) => cmd.id);

  // Combine and deduplicate
  const allCommands = [
    ...new Set([...configuredCommands, ...commonOperations]),
  ];

  // Sort by preferred order: ordered operations first, then the rest alphabetically
  const commands = [
    ...OPERATIONS_ORDER.filter((op) => allCommands.includes(op)),
    ...allCommands.filter((op) => !OPERATIONS_ORDER.includes(op)).sort(),
  ];

  return { commands };
}

// Mirrors OperationKind in api/src/types.rs: the only operation names the
// fleet-operations API accepts. Palette-only commands (lock, edit_tags, …)
// run per-cluster instead.
const FLEET_OPERATION_KINDS = new Set([
  "upgrade",
  "restart",
  "scale",
  "reindex",
  "warm_cache",
  "get_config",
  "set_config",
  "compact_wal",
  "lsm_gc",
  "storage_stats",
  "tidy",
  "recall_replay", // alias; transforms to a tidy operation on submit
  "rolling_restart", // alias; transforms to a restart operation on submit
  "force_restart", // alias; transforms to a restart operation on submit
]);

export function isFleetOperationKind(commandId: string): boolean {
  return FLEET_OPERATION_KINDS.has(commandId);
}

/**
 * Get command configuration by id
 */
export function getCommandConfig(commandId: string): CommandConfig | undefined {
  return COMMAND_CONFIGS.find((cmd) => cmd.id === commandId);
}

/**
 * Get the display name for a command
 */
export function getCommandDisplayName(commandId: string): string {
  const config = getCommandConfig(commandId);
  return config?.displayName || commandId;
}

/**
 * Check if a command is a special action (doesn't execute an operation)
 */
export function isActionCommand(commandId: string): boolean {
  const config = getCommandConfig(commandId);
  return config?.type === "action";
}

/**
 * Get the operation name that should be executed for a given command
 * For regular operations, returns the command id itself; for aliases, the
 * operation they submit as
 */
export function getExecutionOperation(commandId: string): string {
  return getCommandConfig(commandId)?.operation ?? commandId;
}

/** The alias a stored operation was submitted through, if any. */
export function getOperationAlias(
  operation: string,
  args: Record<string, any>,
): CommandConfig | undefined {
  return COMMAND_CONFIGS.find(
    (config) =>
      config.operation === operation && config.matchesOperation?.(args ?? {}),
  );
}

/** The operation's name, or its alias's name if it was submitted through one. */
export function getOperationDisplayName(
  operation: string,
  args: Record<string, any>,
): string {
  return getOperationAlias(operation, args)?.displayName ?? operation;
}

/**
 * The command and form values that resubmit a stored operation. Operations
 * submitted through an alias reopen on that alias when it's available.
 */
export function getRetryOperation(
  operation: string,
  args: Record<string, any>,
  clusters: Cluster[],
): { command: string; arguments: Record<string, any> } {
  const alias = getOperationAlias(operation, args);
  if (alias && (alias.shouldShow?.(clusters) ?? true)) {
    return {
      command: alias.id,
      arguments: alias.retryArguments?.(args) ?? args,
    };
  }
  // Restarts from before all_at_once deleted every pod at once, so they reopen
  // as force-restart.
  if (
    operation === "restart" &&
    clusters.length === 1 &&
    advertisesRestartPods(clusters[0])
  ) {
    return { command: "force_restart", arguments: args };
  }
  return { command: operation, arguments: args };
}

/**
 * Execute a command action (for action-type commands only)
 * Automatically closes the dialog after the action executes
 */
export function executeCommandAction(
  commandId: string,
  clusters: Cluster[],
  handleDialogClose: () => void,
  openModal?: (modalType: any) => void,
  navigate?: (path: string) => void,
): void {
  const config = getCommandConfig(commandId);
  if (config?.type === "action" && config.action) {
    config.action(clusters, openModal, navigate);
    handleDialogClose();
  }
}

export function getArguments(
  commandId: string,
  possibleOperations: Record<string, any>,
): ArgumentDefinition | undefined {
  if (isAliasCommand(commandId)) {
    // Declarative alias - use synthetic arguments
    return getSyntheticArguments(commandId);
  } else {
    // Regular operation - get the operation's arguments
    const operation = possibleOperations[commandId];
    const args = operation?.arguments;
    // Old agents advertise name_prefix as optional, but a blank prefix restarts
    // every workload there.
    if (commandId === "restart" && args?.name_prefix) {
      return { ...args, name_prefix: { ...args.name_prefix, required: true } };
    }
    return args;
  }
}

/**
 * Get synthetic arguments for an alias command
 */
export function getSyntheticArguments(
  commandId: string,
): ArgumentDefinition | undefined {
  const config = getCommandConfig(commandId);
  return config?.syntheticArguments;
}

/**
 * Get the form data transformer for an alias command
 */
export function getFormDataTransformer(
  commandId: string,
): ((jsonData: Record<string, any>) => Record<string, any>) | undefined {
  const config = getCommandConfig(commandId);
  return config?.transformFormData;
}

/**
 * Get the form note for a command
 */
export function getFormNote(
  commandId: string,
  clusters: Cluster[],
): string | undefined {
  if (isAliasCommand(commandId)) {
    const note = getCommandConfig(commandId)?.formNote;
    return typeof note === "function" ? note(clusters) : note;
  } else {
    return operationNotes.get(commandId)?.note;
  }
}

export function isAliasCommand(commandId: string): boolean {
  const config = getCommandConfig(commandId);
  return config?.type === "alias";
}
